import React, {useContext, useEffect, useRef, useState} from "react";
import Error from "../../../../Alerts/Error";
import {fileTypeFromBlob} from 'file-type';
import Spinner from "../../../../Spinner";
import AudioRecorder from "../../AudioRecorder";
import {v4 as uuid} from "uuid";
import { parseBlob } from 'music-metadata';
import { queuePositionLabel } from "../../../../../util/uploadQueue";
import CancelUpload, {useUploadCancel} from "./CancelUpload";
import { claimSelection, releaseClaim, uploadOwner, DUPLICATE_IN_ENTRY_MESSAGE } from "../../../../../util/duplicateFiles";

// A dual-mono file is a 2-channel container whose channels are identical (or, after lossy
// encoding, near-identical). music-metadata reports it as 2 channels -- indistinguishable
// from true stereo by channel count alone. We tell them apart by decoding the PCM and
// measuring the largest L-R difference: true stereo differs loudly (~-13 dB), while dual mono
// differs only by codec residue (below -49 dB on the client's real samples). -40 dB, i.e.
// 0.01 in normalized [-1,1] amplitude, sits comfortably inside that gap.
const DUAL_MONO_MAX_DIFF = 0.01;

const CHANNEL_ERRORS = {
    mono: "Please upload a mono (single-channel) audio file.",
    stereo: "Please upload a stereo audio file (two distinct channels).",
    dualMono: "Please upload a dual-mono audio file (two identical channels).",
    monoStereo: "Please upload a mono or stereo audio file.",
};

function channelMatches(rule, actual) {
    switch (rule) {
        case "mono": return actual === "mono";
        case "stereo": return actual === "stereo";
        case "dualMono": return actual === "dualMono";
        case "monoStereo": return actual === "mono" || actual === "stereo";
        default: return true; // "any" / unknown -> no restriction
    }
}

export default function Audio({formId, setOtherDisabledReason, uploadFile, setFile, fileKey, file}) {
    const fileRef = useRef();
    const audioRef = useRef();
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const upload = useUploadCancel();
    const [progress, setProgress] = useState(null); // {loaded, total, bps, phase} while working, null otherwise
    const speedRef = useRef(null); // {loaded, time, bps} sampler for upload speed

    function getDuration(file) {
        return new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const audio = new window.Audio();
            audio.preload = "metadata";
            audio.src = url;
            audio.onloadedmetadata = () => {
                URL.revokeObjectURL(url);
                resolve(audio.duration);
            };
            audio.onerror = (e) => {
                URL.revokeObjectURL(url);
                reject("Failed to load audio metadata");
            };
        });
    }

    async function getSampleRate(file) {
        const metadata = await parseBlob(file);
        return metadata.format.sampleRate;
    }

    async function getNumberOfChannels(file) {
        const metadata = await parseBlob(file);
        return metadata.format.numberOfChannels;
    }

    // Decode the file and compare its two channels; true when they're identical enough to be
    // dual mono. There's no ffmpeg in the browser, so we decode via the Web Audio API -- the
    // same L-R comparison, on PCM samples.
    async function isDualMono(file) {
        const arrayBuffer = await file.arrayBuffer();
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        try {
            const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
            if (audioBuffer.numberOfChannels < 2) return false;
            const left = audioBuffer.getChannelData(0);
            const right = audioBuffer.getChannelData(1);
            const len = Math.min(left.length, right.length);
            let maxDiff = 0;
            for (let i = 0; i < len; i++) {
                const diff = Math.abs(left[i] - right[i]);
                if (diff > maxDiff) {
                    maxDiff = diff;
                    if (maxDiff > DUAL_MONO_MAX_DIFF) break; // clearly stereo -- stop scanning
                }
            }
            return maxDiff <= DUAL_MONO_MAX_DIFF;
        }
        finally {
            if (ctx.state !== "closed") ctx.close().catch(e => console.warn("AudioContext close failed", e));
        }
    }

    // Clears the chosen file and tells the contributor why. Every rejection goes through here so no
    // path can empty the input while leaving nothing on screen.
    function reject(message) {
        fileRef.current.value = null;
        if (!fileKey) { // if there is already a file, don't hide the audio as it will be removed if upload fails
            audioRef.current.classList.add("d-none");
            audioRef.current.controls = false;
        }
        setErrors([message]);
    }

    async function onChange() {
        // Held here rather than read off the element again after each await: one upload runs at a
        // time and the input is disabled while it does, but the checks below happen before that.
        const chosen = fileRef.current.files[0];
        if (!chosen) {
            return;
        }

        // Turned away the moment it is picked rather than when the queue gets round to hashing it:
        // a file waits minutes for its turn, and the second copy is picked during that wait, while
        // the first is still being checked — which is how this was reported (DEV-3191).
        const owner = uploadOwner(formId, file.id);
        if (!claimSelection(chosen, owner)) {
            return reject(DUPLICATE_IN_ENTRY_MESSAGE);
        }

        // A file the checks below turn away is not this input's after all, so it must stop being
        // held against the others. One that reaches the upload keeps its claim, which the upload
        // itself releases if it fails.
        let reachedUpload = false;

        try {
            setLoading(true);
            setErrors([]);
            const signal = upload.begin();

            let {size} = chosen;
            size /= 1000000;

            const duration = await getDuration(chosen);

            if (file.properties.minSamplingRate || file.properties.maxSamplingRate) {
                const sampleRate = await getSampleRate(chosen);

                if (file.properties.minSamplingRate && sampleRate < Number(file.properties.minSamplingRate)) {
                    return reject(`Please make sure to upload a file with a minimum sample rate of ${file.properties.minSamplingRate} Hz.`);
                }
                if (file.properties.maxSamplingRate && sampleRate > Number(file.properties.maxSamplingRate)) {
                    return reject(`Please make sure to upload a file with a maximum sample rate of ${file.properties.maxSamplingRate} Hz.`);
                }
            }
            const channelRule = file.properties.audioChannels;
            if (channelRule && channelRule !== "any") {
                const numberOfChannels = await getNumberOfChannels(chosen);

                let actual;
                if (numberOfChannels === 1) {
                    actual = "mono";
                } else if (numberOfChannels === 2) {
                    if (channelRule === "mono") {
                        actual = "stereo"; // any 2-channel file already fails a mono-only rule; skip the decode
                    } else {
                        actual = (await isDualMono(chosen)) ? "dualMono" : "stereo";
                    }
                } else {
                    actual = "multichannel";
                }

                if (!channelMatches(channelRule, actual)) {
                    return reject(CHANNEL_ERRORS[channelRule] || "Uploaded file does not meet the required audio channel configuration.");
                }
            }
            if (size > Number(file.properties.maxFileSize)) {
				return reject(`File exceeded maximum file size of ${file.properties.maxFileSize} MB`);
			}
            else if (size < Number(file.properties.minFileSize)) {
				return reject(`File does not have the minimum file size of ${file.properties.minFileSize} MB`);
			}
            else if (duration < Number(file.properties.minAudioLength)) {
				return reject(`Audio duration does not have the minimum length of ${file.properties.minAudioLength} seconds`);
			}
            else if (duration > Number(file.properties.maxAudioLength)) {
				return reject(`Audio duration exceeds the maximum length of ${file.properties.maxAudioLength} seconds`);
			}
            else {
                const fileType = await fileTypeFromBlob(chosen);
                if (fileType && !fileType.mime.startsWith('audio')) {
                    return reject("This is not an audio file.");
                }
                // check for the correct file extension
                if (fileType && !file.properties.extensions.includes('.' + fileType.ext)) {
                    return reject("The extension of your file is not allowed, the allowed extensions are: " + file.properties.extensions.toString());
                }
                //probably a text file then
                if (!fileType) {
                    return reject("This is not an audio file.");
                }

                speedRef.current = null;
                reachedUpload = true;
                const key = await uploadFile(chosen, undefined, (loaded, total, phase, position) => {
                    const now = Date.now();
                    // Restart the sampler whenever the phase changes: waiting in the queue moves
                    // no bytes at all and hashing moves them at disk speed, so neither is a
                    // baseline the wire speed that follows should be measured against.
                    if (!speedRef.current || speedRef.current.phase !== phase) {
                        speedRef.current = {loaded: 0, time: now, bps: 0, phase};
                    }
                    const s = speedRef.current;
                    const dt = (now - s.time) / 1000;
                    if (dt >= 0.5) { // sample twice a second, smooth with an exponential moving average
                        const inst = Math.max(0, loaded - s.loaded) / dt;
                        s.bps = s.bps ? s.bps * 0.6 + inst * 0.4 : inst;
                        s.loaded = loaded;
                        s.time = now;
                    }
                    setProgress({loaded, total, bps: s.bps, phase, position});
                }, signal);

                if (key) {
                    setFile(key, chosen.name);
                    fileRef.current.value = null;
                } else if (fileKey) {
                    // if there is already a file key, and new file is couldn't be uploaded, keep the old file key
                    fileRef.current.value = null;
                } else {
                    fileRef.current.value = null;
                    audioRef.current.classList.add("d-none");
                    audioRef.current.controls = false;
                    setFile('', '');
                }
            }
        }
        catch (e) {
            // Anything unexpected still has to say so. Clearing the input with no message leaves the
            // contributor looking at an empty field, assuming they did something wrong.
            console.error(e);
            reject("Something went wrong while adding your audio. Please try again, and let us know if it keeps happening.");
        }
        finally {
            if (!reachedUpload) {
                releaseClaim(owner);
            }
            upload.end();
            setLoading(false);
            setProgress(null);
        }
    }

    useEffect(() => {
        try {
            audioRef.current.pause();
            if (fileKey) {
                audioRef.current.src = process.env.REACT_APP_ENDPOINT + '/files/' + fileKey + '?redirect=1';
                audioRef.current.controls = true;
                audioRef.current.classList.remove("d-none");
            }
            else {
                audioRef.current.classList.add("d-none");
                audioRef.current.controls = false;
            }
        }
        catch (e) {
            console.error(e);
            audioRef.current.classList.add("d-none");
            audioRef.current.controls = false;
        }
    }, [fileKey]);

    async function addAudio(keyToUpload) {
        try {
            setLoading(true);
            setErrors([]);
            const key = await uploadFile(null, keyToUpload);

            if (key) {
                setFile(key, key);
            }
            else {
                setFile('', '');
            }
        }
        catch (e) {
            console.error(e);
            audioRef.current.classList.add("d-none");
            audioRef.current.controls = false;
            setErrors(["Something went wrong while saving your recording. Please try again."]);
        }
        setLoading(false);
    }

    return (
        <div className={"overflow-x-auto"}>
            <label htmlFor={formId + file.id} className={"form-label"}>{file.name}:</label>


            {file.properties.canRecord ?
                    <AudioRecorder setOtherDisabledReason={setOtherDisabledReason} onBlobReady={addAudio} file={file}/>
                :
                /* Disabled while one is going: a second pick would start a second upload sharing the
                   same connection, and the two would race for this input's single answer. */
                <input disabled={loading} id={formId + file.id} form={formId} required={!fileKey} ref={fileRef} onChange={onChange} type={"file"} className={"form-control"} name={file.name}
                       accept={file.properties.extensions.toString()}/>
            }

            {loading && (progress && progress.total ?
                (() => {
                    const pct = Math.min(100, Math.round((progress.loaded / progress.total) * 100));
                    const bps = progress.bps || 0;
                    const mbps = (bps / 1048576).toFixed(1);
                    const remaining = bps > 0 ? Math.round((progress.total - progress.loaded) / bps) : null;
                    const eta = remaining != null
                        ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`
                        : "—";
                    // An entry can hold dozens of file inputs and only a couple upload at a
                    // time, so a file may be waiting its turn rather than transferring. Say which.
                    const isQueued = progress.phase === "queued";
                    return (
                        <div className="mt-3">
                            <div className="progress" style={{height: "20px"}}>
                                <div className={"progress-bar progress-bar-striped progress-bar-animated" + (isQueued ? " bg-secondary" : "")}
                                     role="progressbar"
                                     style={{width: pct + "%"}}
                                     aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                    {pct}%
                                </div>
                            </div>
                            <small className="text-muted">
                                {isQueued ? queuePositionLabel(progress.position) :
                                    <>
                                        Uploading… {(progress.loaded / 1048576).toFixed(0)} / {(progress.total / 1048576).toFixed(0)} MB
                                        {bps > 0 && ` · ${mbps} MB/s · ~${eta} left`}
                                    </>
                                }
                            </small>
                        </div>
                    );
                })()
                :
                <div className="mt-3">
                    <Spinner />
                    <small className="text-muted ms-2">Preparing upload…</small>
                </div>
            )}
            {upload.active && <CancelUpload onCancel={upload.cancel} />}

            <audio ref={audioRef}/>

            {/* Last, so a refusal reads as this input's and not the next one's. */}
            {errors.map((value, index) => {
                return <Error key={index} message={value}/>;
            })}
        </div>
    );
}
